import React from 'react';
import moment from 'moment';
import { Nullable } from 'global';
import { Theme } from '@mui/material/styles';
import { DateFormats } from 'constants/common';
import { Box, Typography, Stack } from '@mui/material';
import { DealActivityEventPriority, DealActivityStatus } from '@trnsact/trnsact-shared-types';

import { CalendarEventCardProps } from './types';
import { PRIORITY_CONFIG, ACTIVITY_TYPE_LABELS } from './constants';

export const getPriorityColor =
  (priority?: Nullable<DealActivityEventPriority>) =>
  (theme: Theme): string =>
    priority ? PRIORITY_CONFIG[priority].color(theme) : theme.palette.divider;

export const CalendarEventCard = ({ event, sx }: CalendarEventCardProps) => {
  const { title, status, allDay, start, activity } = event;

  const isCancelledOrClosed = status === DealActivityStatus.Closed || status === DealActivityStatus.Cancelled;

  // Activities sit in the all-day band on Day/Week but still happened at a specific time.
  const timeLabel = (!allDay || activity) && start ? moment(start).format(DateFormats.Time12Hour) : 'All Day';

  const activityLabel = activity ? ACTIVITY_TYPE_LABELS[activity.type] : null;

  return (
    <Box
      sx={[
        theme => ({
          display: 'flex',
          overflow: 'hidden',
          alignItems: 'center',
          justifyContent: 'flex-start',
          gap: theme.spacing(0.5),
          padding: theme.spacing(0, 0.5),
        }),
        ...(Array.isArray(sx) ? sx : [sx]),
      ]}
    >
      <Stack direction="row" gap="0.2rem" alignItems="center" sx={{ minWidth: 0, flex: 1 }}>
        <Typography noWrap variant="caption" sx={{ color: 'text.secondary', flexShrink: 0 }}>
          {timeLabel}
        </Typography>

        {activityLabel && (
          <Typography noWrap variant="caption" sx={{ color: 'info.main', fontWeight: 600, flexShrink: 0 }}>
            {activityLabel}
          </Typography>
        )}

        <Typography
          noWrap
          variant="caption"
          sx={{ minWidth: 0, color: 'black', textDecoration: isCancelledOrClosed ? 'line-through' : 'none' }}
        >
          {title}
        </Typography>
      </Stack>
    </Box>
  );
};
